import { baseLang } from './charCount';

// Default page size used when the user has not configured chars_per_page
// in their rate settings. 1800 is the industry-standard "translator's
// page" - 1500 chars + 300 spaces, roughly one A4.
export const DEFAULT_CHARS_PER_PAGE = 1800;

// Money values (rates, amounts, totals) keep up to 4 decimals with no forced
// integer rounding - a per-word rate like 0.05 must not collapse to "0".
// Counts (chars, words, pages) use their own integer/fixed formatters.
export function formatMoney(n) {
  return (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

// Round a money amount to 2 decimals. The single rounding point for invoice
// line amounts so a line's printed value and the summed total always agree.
export function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Compute the client-facing price for a translation job against the user's
 * own rate mapping. Same logic previously inlined in PricePanel; extracted
 * so the Calculator page and PricePanel share one source of truth.
 *
 * Priced per standard page (chars / chars_per_page) or per word, per the
 * rate settings' `unit`. Word counts are only needed for the per-word unit;
 * callers that can't supply them (e.g. no word count for the source side)
 * get `{ rate }` with no price rather than a wrong per-page number.
 *
 * Returns:
 *   - null                                                  no rate settings at all
 *   - { rate: null }                                        pair not covered
 *   - { rate, unit, quantity, price, currency, basis }      fully priced
 *     (per-page results also include `pages` for back-compat)
 *   - { rate } (no price)                                   pair covered but the
 *                                                           basis-side count is 0/missing
 */
export function computePriceFromRates({
  rateSettings,
  sourceChars = 0,
  targetChars = 0,
  sourceWords = 0,
  targetWords = 0,
  sourceLang,
  targetLang,
}) {
  if (!rateSettings || !rateSettings.rates?.length) return null;
  const rate = rateSettings.rates.find(
    (r) =>
      baseLang(r.source_lang) === baseLang(sourceLang) &&
      baseLang(r.target_lang) === baseLang(targetLang)
  );
  if (!rate) return { rate: null };

  const basis = rateSettings.basis === 'target' ? 'target' : 'source';
  const unit = rateSettings.unit === 'word' ? 'word' : 'page';
  const currency = rateSettings.currency || '';

  if (unit === 'word') {
    const words = basis === 'target' ? targetWords : sourceWords;
    if (!words) return { rate };
    return { rate, unit, basis, currency, quantity: words, price: words * rate.price };
  }

  const perPage = rateSettings.chars_per_page || DEFAULT_CHARS_PER_PAGE;
  const chars = basis === 'target' ? targetChars : sourceChars;
  if (!chars) return { rate };
  const pages = chars / perPage;
  return { rate, unit, basis, currency, quantity: pages, pages, price: pages * rate.price };
}
